/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandableSection from '../../expandableSection';
import { SummaryRing, normalizeStatusClass } from './sectionShared';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import CustomDatePicker from '../../customDatePicker';
import { useSnackbar } from '../../../../../Components/Snackbar/snackBarProvider';
import dayjs from 'dayjs';
import { createRequest } from '../../../../../Api/request/staffingRequest';
const SHOW_RESOURCE_SUMMARY = false;

const normalizeDateForApi = (value) => {
  if (!value) return '';
  if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const toOptionalPositiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const formatLegacyAllocationDate = (value) => {
  const normalized = normalizeDateForApi(value);
  return normalized ? dayjs(normalized).format('DD-MMM-YYYY') : '-';
};

const buildLegacyBulkRemark = (row, comments) => {
  const trimmedComments = String(comments || '').trim();
  const employeeRef = `${row?.employeeId || ''}-${row?.name || ''}`;
  const allocationText = `${Number(row?.allocation || 0)}%`;
  const startDateText = formatLegacyAllocationDate(row?.startDateValue || row?.startDate);
  const endDateText = formatLegacyAllocationDate(row?.endDateValue || row?.endDate);
  const base = [
    `(Change for ${employeeRef} with existing allocation ${allocationText},`,
    `S.D ${startDateText} and E.D ${endDateText})`,
  ].join(' ');
  return trimmedComments ? `${base} ${trimmedComments}.` : `${base}.`;
};

const assertApiSuccess = (response, fallbackMessage) => {
  const responseData = response?.data;
  if (
    !responseData
    || responseData?.name === 'AxiosError'
    || responseData?.isAxiosError
  ) {
    throw responseData || new Error(fallbackMessage);
  }
  if (typeof responseData === 'object' && responseData !== null && responseData.success === false) {
    throw new Error(responseData.message || fallbackMessage);
  }
};

const splitDateAndDay = (value = '') => {
  const parts = String(value || '').split(',');
  if (parts.length < 2) {
    return { date: value || '', day: '' };
  }

  return {
    date: parts.slice(1).join(',')
      .trim(),
    day: parts[0].trim(),
  };
};

const getResourceRowId = (row, fallbackIndex = 0) => (
  [
    row?._groupKey,
    row?._isGroupChild ? 'child' : 'parent',
    row?._childIndex ?? 'root',
    row?.id,
    row?.employeeId,
    row?.name,
    row?.role,
    row?.skill,
    row?.startDate,
    row?.endDate,
    row?.allocationSortValue,
    fallbackIndex,
  ].filter((value) => value !== null && value !== undefined && value !== '').join('__')
);

const normalizeResourceGroupKeyPart = (value) => String(value || '').trim()
  .toLowerCase();

const getResourceGroupKey = (row, index) => {
  const employeeKey = normalizeResourceGroupKeyPart(row?.employeeId);
  if (employeeKey) return employeeKey;

  const nameKey = normalizeResourceGroupKeyPart(row?.name);
  if (nameKey) return nameKey;

  return `resource-${index}`;
};

const isMovedOutResource = (row) => normalizeResourceGroupKeyPart(row?.status) === 'moved out';

const isActiveResource = (row) => normalizeResourceGroupKeyPart(row?.status) === 'active';

const canShowRowCheckbox = (row) => row?._isGroupChild;

const canSelectResourceRow = (row) => canShowRowCheckbox(row) && isActiveResource(row);
const getResourceStatusPriority = (status = '') => {
  const normalizedStatus = String(status || '').trim()
    .toLowerCase();

  if (normalizedStatus === 'active') return 0;
  if (normalizedStatus === 'shadow') return 0;
  if (normalizedStatus === 'moved out') return 2;
  return 1;
};

const getResourceDateSortValue = (row) => {
  const value = row?.startDateValue || row?.endDateValue;
  if (!value) return Number(row?.allocationSortValue || 0);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : Number(row?.allocationSortValue || 0);
};

const ResourceSummary = ({ resourceRingData, resourceAllocationData }) => (
  <div className="resource-summary resource-summary-v2">
    <div className="summary-box total-card">
      <div className="total-ring">
        <SummaryRing segments={resourceRingData} />
        <div className="total-text">
          <span>Total</span>
          <strong>{resourceAllocationData?.summary?.total || 0}</strong>
        </div>
      </div>
      <div className="total-breakdown">
        <div className="breakdown-item">
          <span className="dot open" />
          <span>Open</span>
          <strong>{resourceAllocationData?.summary?.open || 0}</strong>
        </div>
        <div className="breakdown-item">
          <span className="dot closed" />
          <span>Closed</span>
          <strong>{resourceAllocationData?.summary?.closed || 0}</strong>
        </div>
        <div className="breakdown-item">
          <span className="dot transferred" />
          <span>Transferred</span>
          <strong>{resourceAllocationData?.summary?.transferred || 0}</strong>
        </div>
      </div>
    </div>

    <div className="summary-box">
      <div className="icon-box red">
        <span className="icon">&#128101;</span>
        <span className="icon-label">Resource Allocated</span>
        <span className="icon-count">{resourceAllocationData?.summary?.allocated || 0}</span>
      </div>
    </div>

    <div className="summary-box">
      <div className="icon-box yellow">
        <span className="icon">&#128197;</span>
        <span className="icon-label">Allocated Days</span>
        <span className="icon-count">{resourceAllocationData?.summary?.days || 0}</span>
      </div>
    </div>

    <div className="summary-box">
      <div className="icon-box green">
        <span className="icon">&#9200;</span>
        <span className="icon-label">Allocated Hours</span>
        <span className="icon-count">{resourceAllocationData?.summary?.hours || 0}</span>
      </div>
    </div>

    <div className="summary-box">
      <div className="icon-box orange">
        <span className="icon">&#9888;</span>
        <span className="icon-label">Overdue open issues</span>
        <span className="icon-count">{resourceAllocationData?.summary?.overdue || 0}</span>
      </div>
    </div>
  </div>
);

const ResourceTableToolbar = ({
  areAllGroupsExpanded,
  hideMovedOut,
  onHideMovedOutChange,
  selectedResourceRows = [],
  isBulkUpdating,
  hasSelection,
  onBulkChange,
  toggleAllEmployeeGroups,
}) => (
  <div className="resource-table-toolbar">
    <div className="resource-toolbar-left">
      <label className="resource-filter-toggle">
        <input
          type="checkbox"
          checked={hideMovedOut}
          onChange={(event) => onHideMovedOutChange(event.target.checked)}
        />
        <span>Hide moved out</span>
      </label>
      <label className="resource-filter-toggle">
        <input
          type="checkbox"
          checked={areAllGroupsExpanded}
          onChange={(event) => toggleAllEmployeeGroups(event.target.checked)}
        />
        <span>Expand details</span>
      </label>
    </div>
    <div className= "resource-bulk-right-wrapper">
      <span className="bulk-change-info">Number of resources selected: {selectedResourceRows.length}</span>
      <button type="button" disabled={!hasSelection || isBulkUpdating}
        className={`bulk-change-btn ${!hasSelection || isBulkUpdating ? 'disabled' : ''}`}
        onClick={() => onBulkChange(selectedResourceRows)}>
        Bulk Change
      </button>
    </div>
  </div>
);

const ResourceTable = ({
  displayRows,
  onChangeEndDate,
  resourceAllocationData,
  selectedResourceRows,
  setSelectedResourceRows,
  toggleEmployeeGroup,
  isBulkUpdating,
}) => {
  const selectableRowIds = useMemo(
    () => displayRows.flatMap((row, idx) => (
      canSelectResourceRow(row) ? [getResourceRowId(row, idx)] : []
    )),
    [displayRows],
  );
  const selectedRowsSet = new Set(selectedResourceRows);
  const hasSelectableRows = selectableRowIds.length > 0;
  const areAllSelectableRowsSelected = hasSelectableRows
    && selectableRowIds.every((rowId) => selectedRowsSet.has(rowId));

  const handleSelectAllRows = (checked) => {
    setSelectedResourceRows(checked ? selectableRowIds : []);
  };

  const handleSelectRow = (rowId, checked) => {
    setSelectedResourceRows((prev) => (
      checked
        ? [...new Set([...prev, rowId])]
        : prev.filter((selectedRowId) => selectedRowId !== rowId)
    ));
  };

  return (
    <div className="resource-table-wrapper">
      <div className="resource-table-scroll">
        <table className="resource-data-table">
          <thead>
            <tr>
              <th className="th-cell checkbox-col">
                <input
                  type="checkbox"
                  aria-label="Select all non moved out resources"
                  checked={areAllSelectableRowsSelected}
                  disabled={!hasSelectableRows}
                  onChange={(event) => handleSelectAllRows(event.target.checked)}
                />
              </th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Employee name</span>
                </div>
              </th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Role</span>
                </div>
              </th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Skills</span>
                </div>
              </th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Allocation %</span>
                </div>
              </th>
              <th className="th-cell">Start date</th>
              <th className="th-cell">End date</th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Allocated days</span>
                </div>
                <div className="header-total">
                  {Number(resourceAllocationData?.summary?.days || 0).toFixed(2)}
                </div>
              </th>
              <th className="th-cell">
                <div className="th-content">
                  <span>Allocated hours</span>
                </div>
                <div className="header-total">
                  {Number(resourceAllocationData?.summary?.hours || 0).toFixed(2)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => {
              const rowId = getResourceRowId(row, idx);
              const showCheckbox = canShowRowCheckbox(row);
              const isRowSelectable = canSelectResourceRow(row);
              const showChangeEndDateLink = row._isGroupChild && typeof onChangeEndDate === 'function';

              return (
                <tr
                  key={rowId}
                  className={`tr-data ${row._isGroupChild ? 'child-row' : ''}`.trim()}
                >
                  <td className="td-cell checkbox-col">
                    {showCheckbox ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.name || row.employeeId || 'resource'}`}
                        checked={isRowSelectable && selectedRowsSet.has(rowId)}
                        disabled={!isRowSelectable}
                        onChange={(event) => handleSelectRow(rowId, event.target.checked)}
                      />
                    ) : null}
                  </td>
                  <td className="td-cell">
                    <div className="employee-info">
                      <div className="employee-name-row">
                        {!row._isGroupChild ? (
                          <button
                            type="button"
                            className={`resource-expand-btn ${row._isExpanded ? 'expanded' : ''}`}
                            aria-label={row._isExpanded ? 'Collapse employee rows' : 'Expand employee rows'}
                            aria-expanded={row._isExpanded}
                            onClick={() => toggleEmployeeGroup(row._groupKey)}
                          >
                        &#8250;
                          </button>
                        ) : (
                          <span className="resource-expand-spacer" aria-hidden="true" />
                        )}
                        {!row._isGroupChild ? (
                          <span className="employee-name employee-name-inline">{row.name || ''}</span>
                        ) : (
                          <span className="employee-name employee-name-placeholder" aria-hidden="true" />
                        )}
                      </div>
                      {!row._isGroupChild ? (
                        <span className="employee-id employee-id-below">{row.employeeId || ''}</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="td-cell">
                    <div className="role-info">
                      <div className="role-name">{row.role || ''}</div>
                      <span className={`status-badge role-status-badge ${normalizeStatusClass(row.status)}`}>
                        {row.status || ''}
                      </span>
                    </div>
                  </td>
                  <td className="td-cell">{row.skill || ''}</td>
                  <td className="td-cell">{row.allocation || 0}%</td>
                  <td className="td-cell">
                    <div className="resource-date-cell">
                      <span className="date-main">{splitDateAndDay(row.startDate).date}</span>
                      <span className="date-day">{splitDateAndDay(row.startDate).day}</span>
                    </div>
                  </td>
                  <td className="td-cell">
                    <div className="resource-date-cell">
                      <span className="date-main">{splitDateAndDay(row.endDate).date}</span>
                      <span className="date-day">{splitDateAndDay(row.endDate).day}</span>
                      {showChangeEndDateLink ? (
                        <button
                          type="button"
                          disabled={selectedResourceRows.length > 0 || isBulkUpdating}
                          className= {`resource-change-link 
                            ${selectedResourceRows.length > 0 || isBulkUpdating ? 'disabled' : ''}`}
                          aria-label={`Change end date for ${row.name || row.employeeId || 'resource'}`}
                          onClick={() => onChangeEndDate(row)}
                        >
                          Change
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="td-cell allocated-value">
                    {Number(row.allocatedDays || 0).toFixed(2)}
                  </td>
                  <td className="td-cell allocated-value">
                    {Number(row.allocatedHours || 0).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ResourceSummary.propTypes = {
  resourceRingData: PropTypes.array.isRequired,
  resourceAllocationData: PropTypes.shape({
    summary: PropTypes.shape({
      total: PropTypes.number,
      open: PropTypes.number,
      closed: PropTypes.number,
      transferred: PropTypes.number,
      allocated: PropTypes.number,
      days: PropTypes.number,
      hours: PropTypes.number,
      overdue: PropTypes.number,
    }),
  }).isRequired,
};

ResourceTableToolbar.propTypes = {
  areAllGroupsExpanded: PropTypes.bool.isRequired,
  hideMovedOut: PropTypes.bool.isRequired,
  onHideMovedOutChange: PropTypes.func.isRequired,
  selectedResourceRows: PropTypes.array.isRequired,
  isBulkUpdating: PropTypes.bool.isRequired,
  hasSelection: PropTypes.bool.isRequired,
  onBulkChange: PropTypes.func.isRequired,
  toggleAllEmployeeGroups: PropTypes.func.isRequired,
};

ResourceTable.propTypes = {
  displayRows: PropTypes.array.isRequired,
  onChangeEndDate: PropTypes.func,
  resourceAllocationData: PropTypes.shape({
    summary: PropTypes.shape({
      days: PropTypes.number,
      hours: PropTypes.number,
    }),
  }).isRequired,
  selectedResourceRows: PropTypes.array.isRequired,
  setSelectedResourceRows: PropTypes.func.isRequired,
  toggleEmployeeGroup: PropTypes.func.isRequired,
  isBulkUpdating: PropTypes.bool.isRequired,
  hasSelection: PropTypes.bool.isRequired,
};

const ResourceDetailsSection = ({
  expanded,
  onToggle,
  onChangeEndDate,
  onDownload,
  onMoreMenuItems,
  showMoreAction,
  resourceRingData,
  resourceAllocationData,
  resourceSortedData,
  canBulkChange,
  projectId,
  projectVertical,
  onRefreshData,
  isLoading,
}) => {
  const { snackbarOpen } = useSnackbar();
  const [hideMovedOut, setHideMovedOut] = useState(false);
  const [expandedEmployeeGroups, setExpandedEmployeeGroups] = useState({});
  const [selectedResourceRows, setSelectedResourceRows] = useState([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const hasSelection = selectedResourceRows.length > 0;
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({ endDate: '', comments: '' });
  const visibleResourceRows = useMemo(
    () => (
      hideMovedOut
        ? resourceSortedData.filter((row) => !isMovedOutResource(row))
        : resourceSortedData
    ),
    [hideMovedOut, resourceSortedData],
  );
  const groupedResourceRows = useMemo(() => {
    const groups = [];
    const groupMap = new Map();

    visibleResourceRows.forEach((row, index) => {
      const groupKey = getResourceGroupKey(row, index);
      const existingGroup = groupMap.get(groupKey);
      if (existingGroup) {
        existingGroup.rows.push(row);
        return;
      }

      const nextGroup = {
        key: groupKey,
        rows: [row],
      };
      groupMap.set(groupKey, nextGroup);
      groups.push(nextGroup);
    });

    groups.forEach((group) => {
      group.rows.sort(
        (firstRow, secondRow) => {
          const statusPriorityDifference = getResourceStatusPriority(firstRow?.status)
            - getResourceStatusPriority(secondRow?.status);
          if (statusPriorityDifference !== 0) {
            return statusPriorityDifference;
          }

          return getResourceDateSortValue(secondRow) - getResourceDateSortValue(firstRow);
        },
      );
    });

    return groups.sort((firstGroup, secondGroup) => {
      const firstStatus = firstGroup?.rows?.[0]?.status;
      const secondStatus = secondGroup?.rows?.[0]?.status;
      const statusPriorityDifference = getResourceStatusPriority(firstStatus)
        - getResourceStatusPriority(secondStatus);

      if (statusPriorityDifference !== 0) {
        return statusPriorityDifference;
      }

      return getResourceDateSortValue(secondGroup?.rows?.[0])
        - getResourceDateSortValue(firstGroup?.rows?.[0]);
    });
  }, [visibleResourceRows]);
  const displayRows = useMemo(() => groupedResourceRows.flatMap((group) => {
    const [parentRow] = group.rows;
    const isExpanded = Boolean(expandedEmployeeGroups[group.key]);
    const rows = [{
      ...parentRow,
      _groupKey: group.key,
      _hasChildren: true,
      _isExpanded: isExpanded,
      _isGroupChild: false,
    }];

    if (!isExpanded) return rows;

    return rows.concat(group.rows.map((row) => ({
      ...row,
      _groupKey: group.key,
      _hasChildren: false,
      _isExpanded: false,
      _isGroupChild: true,
      _childIndex: getResourceRowId(row),
    })));
  }), [expandedEmployeeGroups, groupedResourceRows]);

  const toggleEmployeeGroup = (groupKey) => {
    setExpandedEmployeeGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };
  const expandableGroupKeys = useMemo(
    () => groupedResourceRows.map((group) => group.key),
    [groupedResourceRows],
  );
  const areAllGroupsExpanded = expandableGroupKeys.length > 0
    && expandableGroupKeys.every((groupKey) => expandedEmployeeGroups[groupKey]);
  const toggleAllEmployeeGroups = (checked) => {
    setExpandedEmployeeGroups(
      checked
        ? expandableGroupKeys.reduce((acc, groupKey) => ({ ...acc, [groupKey]: true }), {})
        : {},
    );
  };
  const selectedResourceRowDetails = useMemo(() => {
    const selectedRowIds = new Set(selectedResourceRows);
    return displayRows.filter(
      (row, index) => canSelectResourceRow(row) && selectedRowIds.has(getResourceRowId(row, index)),
    );
  }, [displayRows, selectedResourceRows]);

  const handleOpenBulkChange = () => {
    setIsBulkUpdating(true);
    setIsUpdateDialogOpen(true);
    setBulkFormData({ endDate: '', comments: '' });
  };

  const handleBulkChange = async () => {
    if (!projectId) {
      snackbarOpen('Missing project details for staffing request.', 'error');
      return;
    }
    if (!selectedResourceRowDetails.length) {
      snackbarOpen('Please select at least one resource.', 'error');
      return;
    }

    const normalizedEndDate = normalizeDateForApi(bulkFormData.endDate);
    if (!normalizedEndDate) {
      snackbarOpen('Please select an end date.', 'error');
      return;
    }

    setIsBulkUpdating(true);
    try {
      await Promise.all(selectedResourceRowDetails.map((row) => createRequest({
        project_id: Number(projectId || row?.projectId),
        start_date: dayjs().format('YYYY-MM-DD'),
        end_date: normalizedEndDate,
        skills: '',
        remarks: buildLegacyBulkRemark(row, bulkFormData.comments),
        resource_no: 1,
        allocation_per: Number(row?.allocation || 0) || 0,
        resource_type_id: toOptionalPositiveNumber(row?.resourceTypeId),
        vertical: projectVertical || '',
        experience: '',
        good_skills: '',
      }).then((response) => {
        assertApiSuccess(response, 'Failed to create staffing request.');
        return response;
      })));

      snackbarOpen(
        selectedResourceRowDetails.length === 1
          ? 'Staffing request created successfully.'
          : `Created ${selectedResourceRowDetails.length} staffing requests successfully.`,
        'success',
      );
      setSelectedResourceRows([]);
      setIsUpdateDialogOpen(false);
      setBulkFormData({ endDate: '', comments: '' });
      if (typeof onRefreshData === 'function') onRefreshData();
    } catch (err) {
      const fallbackMessage = err?.message || 'Failed to create staffing requests.';
      snackbarOpen(fallbackMessage, 'error');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleDialogClose = () => {
    setIsUpdateDialogOpen(false);
    setIsBulkUpdating(false);
    setBulkFormData({ endDate: '', comments: '' });
  };

  const getValidDayjs = (dateStr) => {
    if (!dateStr || dateStr === '-' || !dayjs(dateStr).isValid()) return null;
    return dayjs(dateStr);
  };

  const getCurrentDateFormatted = () => dayjs().startOf('day');

  return (
    <>
      {isUpdateDialogOpen && (
        <Dialog
          className="update-allocation-dialog"
          header="Change Allocation"
          style={{ width: '50vw', maxWidth: '95vw' }}
          breakpoints={{
            '960px': '75vw',
            '768px': '95vw',
            '560px': '100vw',
          }}
          modal
          visible={isUpdateDialogOpen}
          onHide={handleDialogClose}
          blockScroll={true}
          footer={(
            <div className="custom-dialog-footer">
              <button
                type="button"
                className="p-button p-component p-button-secondary"
                onClick={handleDialogClose}
              >
          Cancel
              </button>

              {canBulkChange ? (
                <button
                  type="button"
                  className="p-button p-component p-button-primary"
                  onClick={handleBulkChange}
                >
            Save
                </button>
              ) : (
                <div className="update-btn-wrapper">
                  <button
                    type="button"
                    className="p-button p-component p-button-primary update-btn-disabled"
                    disabled
                    tabIndex={-1}
                  >
              Save
                  </button>
                  <span className="update-tooltip">
              You do not have permission to update resource allocation details.
                  </span>
                </div>
              )}
            </div>
          )}
        >
          <div className="details-wrapper">
            <h3>No of resources selected :</h3>
            <span>{selectedResourceRowDetails.length}</span>
          </div>

          <div className="form-field">
            <CustomDatePicker
              label="End Date"
              value={getValidDayjs(bulkFormData.endDate)}
              minDate={getCurrentDateFormatted()}
              onChange={(value) => {
                setBulkFormData((prev) => ({
                  ...prev,
                  endDate: normalizeDateForApi(value),
                }));
              }}
              displayFormat="DD-MM-YYYY"
            />
          </div>

          <div className="form-field full-width">
            <label>Comment</label>

            <InputTextarea
              rows={4}
              placeholder="Enter..."
              className="comment-text-area"
              value={bulkFormData.comments}
              onChange={(event) => {
                setBulkFormData((prev) => ({
                  ...prev,
                  comments: event.target.value,
                }));
              }}
            />
          </div>
        </Dialog>
      )}
      <ExpandableSection
        title="Resource allocation details"
        expanded={expanded}
        onToggle={onToggle}
        onDownload={onDownload}
        showMoreAction={showMoreAction}
        onMoreMenuItems={onMoreMenuItems}
        isLoading={isLoading}
      >
        <div className="section-content resource-details-section">
          {SHOW_RESOURCE_SUMMARY ? (
            <ResourceSummary
              resourceRingData={resourceRingData}
              resourceAllocationData={resourceAllocationData}
            />
          ) : null}
          <ResourceTableToolbar
            areAllGroupsExpanded={areAllGroupsExpanded}
            hideMovedOut={hideMovedOut}
            onHideMovedOutChange={setHideMovedOut}
            selectedResourceRows={selectedResourceRows}
            isBulkUpdating={isBulkUpdating}
            hasSelection={hasSelection}
            onBulkChange={handleOpenBulkChange}
            toggleAllEmployeeGroups={toggleAllEmployeeGroups}
          />
          <ResourceTable
            displayRows={displayRows}
            onChangeEndDate={onChangeEndDate}
            resourceAllocationData={resourceAllocationData}
            selectedResourceRows={selectedResourceRows}
            setSelectedResourceRows={setSelectedResourceRows}
            toggleEmployeeGroup={toggleEmployeeGroup}
            isBulkUpdating={isBulkUpdating}
            hasSelection={hasSelection}
          />
        </div>
      </ExpandableSection>
    </>
  );
};

ResourceDetailsSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onChangeEndDate: PropTypes.func,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onMoreMenuItems: PropTypes.array,
  showMoreAction: PropTypes.bool,
  resourceRingData: PropTypes.array.isRequired,
  resourceAllocationData: PropTypes.object.isRequired,
  resourceSortedData: PropTypes.array.isRequired,
  canBulkChange: PropTypes.bool.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectVertical: PropTypes.string,
  onRefreshData: PropTypes.func,
  isLoading: PropTypes.bool,
};

ResourceDetailsSection.defaultProps = {
  isLoading: false,
};

export default ResourceDetailsSection;
